import React from 'react';
import './jobListFilters.scss';
import Button from '../../../../components/button/button';
import Dropdown from '../../../../components/dropdown/dropdown';
import Filter, { RadioButtonOption } from '../../../../components/filter/filter';
import { MultiChipSelectOption } from '../../../../components/filters/multiChipSelect/multiChipSelect';
import { __capitalizeFirstLetter } from '../../../../utils/utils';
import { ActiveFilter } from '../../../crawler.interface';
import { JobStatus } from 'src/app/crawler/crawler.enums';
import CreatedAtDateFilter from './createdAtDateFilter';

// Dropdown == > Wrapper
//  If displayedFilter does not exist:
//      show filer options
//      select one  (addFilter(option))
//      Reset displayedFilter to ''
//  If displayedFilter == ''
//      Show 'added' filter
//      chose value
//      submit

// This component should be responsible for:
//      1. Choosing a filter
//      2. Selecting a filter value from a list of options
//      3. Submitting that value upwards to parent component
export interface JobListFiltersProps {
    activeFilters: ActiveFilter[];
    dropdownRef: React.RefObject<HTMLDivElement>;
    filterOptions: string[];
    isFilterDropdownOpen: boolean;
    crawlfishUsers?: string[];
    submitFilterValue: (filter: ActiveFilter) => void;
}

const JobListFilters = ({ activeFilters, dropdownRef, filterOptions, isFilterDropdownOpen, crawlfishUsers, submitFilterValue }: JobListFiltersProps) => {
    const [displayedFilter, setDisplayedFilter] = React.useState<string>('');
    const [selectedStatusChips, setSelectedStatusChips] = React.useState<MultiChipSelectOption[]>([]);

    function addFilter(filterType: string) {
        setDisplayedFilter(filterType);
    }

    const reviewedRadioButtons: RadioButtonOption[] = [
        { label: 'Reviewed', value: 'true', checked: false },
        { label: 'Un-reviewed', value: 'false', checked: true },
        { label: 'Both', value: 'clear-filter', checked: false }
    ];

    const multiChipOptions: MultiChipSelectOption[] = [
        { name: JobStatus.Complete, value: JobStatus.Complete, label: 'Complete' },
        { name: JobStatus.Queueing, value: JobStatus.Queueing, label: 'Queueing' },
        { name: JobStatus.Active, value: JobStatus.Active, label: 'Active' },
        { name: JobStatus.Failed, value: JobStatus.Failed, label: 'Failed' },
        { name: JobStatus.Canceled, value: JobStatus.Canceled, label: 'Canceled' }
    ];

    React.useEffect(() => {
        init();
    }, [activeFilters]);

    function init() {
        assignSelectedChips();
    }

    function assignSelectedChips() {
        const statusFilterIndex = activeFilters.findIndex((filter: ActiveFilter) => filter.name === 'status');
        if (statusFilterIndex !== -1) {
            const values = activeFilters[statusFilterIndex].value.split('|');
            const chips: MultiChipSelectOption[] = [];
            values.map((val: string) => {
                const label = __capitalizeFirstLetter(val);
                chips.push({ name: val, value: val, label: label });
            });
            setSelectedStatusChips([...chips]);
        }
    }

    let currentFilter;
    switch (displayedFilter) {
        case 'Name':
            currentFilter = <Filter title="Job Name" label="Name contains:" name="name" filterType="text"
                onSubmit={submitFilterValue} closeFilter={() => setDisplayedFilter('')} />;
            break;
        case 'Owner':
            currentFilter = <Filter title="Job Owner" label="Job Owner(s):" name="owner" filterType="multi-select"
                onSubmit={submitFilterValue} closeFilter={() => setDisplayedFilter('')} typeAheadOptions={crawlfishUsers} />;
            break;
        case 'Reviewed':
            currentFilter = <Filter title="Reviewed Status" label="Reviewed Status" name="reviewed" filterType="radio"
                defaultRadioSelection={{ label: 'Un-reviewed', value: 'false', checked: true }} radioButtonOptions={reviewedRadioButtons}
                onSubmit={submitFilterValue} closeFilter={() => setDisplayedFilter('')} />;
            break;
        case 'Status':
            currentFilter = <Filter title="Job Status" label="Job Status" name="status" filterType="multi-chip" defaultSelectedChips={selectedStatusChips}
                multiChipOptions={multiChipOptions} onSubmit={submitFilterValue} closeFilter={() => setDisplayedFilter('')} />;
            break;
        case 'Created Date Range':
            currentFilter = <CreatedAtDateFilter activeFilters={activeFilters} submitFilterValue={submitFilterValue}></CreatedAtDateFilter>;
            break;
        case 'URI':
            currentFilter = <Filter title="URI" label="Job with URI containing:" name="requests" filterType="text"
                onSubmit={submitFilterValue} closeFilter={() => setDisplayedFilter('')} />;
            break;
        default:
            currentFilter = <span style={{ color: 'red' }}>Invalid Filter Selected</span>;
    }

    return (
        <Dropdown isOpen={isFilterDropdownOpen} hasSubmit={false} ref={dropdownRef}>
            <>
                {displayedFilter?.length < 1 ?
                    <div className="dropdown-display-options">
                        {filterOptions.map((option: string) => (
                            <span key={option} className="dropdown-option">
                                <Button variant='submit-filter-button' onClick={() => addFilter(option)}>{option}</Button>
                            </span>
                        ))}
                    </div>
                    :
                    <>
                        {currentFilter}
                    </>
                }
            </>
        </Dropdown>
    );
};

export default JobListFilters;